/**
 * dev-env-shape.js — pure view-model helpers for the customer "Dev
 * Environments" section (`/my/dev-environments`, P8).
 *
 * Everything load-bearing in the section lives here so `node --test` covers it
 * without a DOM: status/kind mapping, the row shape the list + detail render,
 * workspace-action enablement, the node-picker ranking (ported from the
 * operator card in cluster-api `static/js/uts_instance_detail.js`), the create
 * form validation + payload, the FULL API error-code → copy table, and the
 * list-poll cadence.
 *
 * Backend contract mirrored: cluster-api routes/dev_environments.py (P6) —
 * `to_public(env)` docs, `GET /<id>/workspace`, the create/start/stop/delete
 * payloads, and the error codes listed in `errorCopy`.
 *
 * Only relative imports of other PURE utils — no `@/` aliases.
 */
import { apiErrorCode } from './dev-env-api-core.js'
import { isCsApplication } from './instance-shape.js'
import { SECTION_MASK_KEYS, MASKING_KEY } from './secret-masking.js'

// ── Status / kind vocab ─────────────────────────────────────────────────────
// Stored statuses: requested | provisioning | active | paused | stopping |
// stopped | failed. Customer-facing labels collapse them into five states.
export const STATUS_META = Object.freeze({
  requested: { label: 'Creating', tone: 'blue', spinner: true },
  provisioning: { label: 'Creating', tone: 'blue', spinner: true },
  active: { label: 'Running', tone: 'green', spinner: false },
  paused: { label: 'Suspended', tone: 'grey', spinner: false },
  stopped: { label: 'Suspended', tone: 'grey', spinner: false },
  stopping: { label: 'Stopping', tone: 'amber', spinner: true },
  failed: { label: 'Error', tone: 'red', spinner: false }
})

const UNKNOWN_STATUS = Object.freeze({ label: 'Unknown', tone: 'grey', spinner: false })

export const KIND_META = Object.freeze({
  cs_application_dev_mode: { label: 'App', icon: 'fa-cube' },
  catalog_component_dev_mode: { label: 'Catalog', icon: 'fa-cubes' },
  platform_service_isolated: { label: 'Platform service', icon: 'fa-server' }
})

const STARTABLE = new Set(['stopped', 'failed'])
const STOPPABLE = new Set(['active', 'provisioning'])
const TRANSITIONAL = new Set(['requested', 'provisioning', 'stopping'])

/** Meta for a stored status (falls back to a neutral "Unknown"). */
export function statusMeta(status) {
  return STATUS_META[status] || { ...UNKNOWN_STATUS, label: status ? String(status) : 'Unknown' }
}

/** Customer label for a kind (`ui.kind_label` wins when the API sends one). */
export function kindLabel(kind, ui) {
  if (ui && typeof ui.kind_label === 'string' && ui.kind_label) return ui.kind_label
  return (KIND_META[kind] && KIND_META[kind].label) || kind || 'Environment'
}

// ── Time helpers ────────────────────────────────────────────────────────────
const MIN = 60 * 1000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

/**
 * Compact relative time: 'just now' | '4 min ago' | '3 h ago' | '2 d ago' |
 * a short date beyond 30 days. Returns '—' for null/invalid input. `now` is
 * injectable for tests.
 */
export function relativeTime(iso, now = Date.now()) {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const diff = Math.max(0, now - t)
  if (diff < MIN) return 'just now'
  if (diff < HOUR) return `${Math.floor(diff / MIN)} min ago`
  if (diff < DAY) return `${Math.floor(diff / HOUR)} h ago`
  if (diff < 30 * DAY) return `${Math.floor(diff / DAY)} d ago`
  const d = new Date(t)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Row shape ───────────────────────────────────────────────────────────────
/** Hint shown beside a Suspended pill when the operator switched it off. */
export function statusReasonHint(env) {
  const reason = env && env.status_reason
  if (reason === 'external_disable') {
    return 'Switched off from the operator card — you can start it again.'
  }
  return null
}

/**
 * Normalise a public env doc into the row the list/detail render. Every field
 * is defensive — a partially-populated doc (e.g. `requested` before the engine
 * fills `workspace`) must not throw.
 */
export function toEnvRow(env, now = Date.now()) {
  const e = env && typeof env === 'object' ? env : {}
  const id = e.id != null ? String(e.id) : e._id != null ? String(e._id) : null
  const target = e.target && typeof e.target === 'object' ? e.target : {}
  const workspace = e.workspace && typeof e.workspace === 'object' ? e.workspace : {}
  const lifecycle = e.lifecycle && typeof e.lifecycle === 'object' ? e.lifecycle : {}
  const observed = e.observed && typeof e.observed === 'object' ? e.observed : {}
  const status = typeof e.status === 'string' ? e.status : 'unknown'
  const meta = statusMeta(status)
  const vscodeUrl = typeof workspace.code_server_url === 'string' && workspace.code_server_url
    ? workspace.code_server_url : null
  const vscodeLikelyReady = status === 'active' && Boolean(vscodeUrl) && observed.workspace_ready !== false
  const lastActiveAt = lifecycle.last_active_at || null

  return {
    id,
    name: e.name || target.instance_name || target.workload || (id ? `Environment ${id}` : 'Environment'),
    kind: e.kind || null,
    kindLabel: kindLabel(e.kind, e.ui),
    status,
    statusMeta: meta,
    statusLabel: (e.ui && e.ui.status_label) || meta.label,
    statusReason: e.status_reason || null,
    statusHint: statusReasonHint(e),
    placement: {
      cluster: target.cluster || null,
      namespace: target.namespace || null,
      node: target.node || null,
      workload: target.workload || null
    },
    lastActiveAt,
    lastActiveAgo: relativeTime(lastActiveAt, now),
    instanceId: target.instance_id != null ? String(target.instance_id) : null,
    instanceName: target.instance_name || null,
    vscodeUrl,
    vscodeLikelyReady,
    appUrl: typeof workspace.app_url === 'string' && workspace.app_url ? workspace.app_url : null,
    errorMessage: e.error && typeof e.error === 'object' && e.error.message ? String(e.error.message) : null,
    // Provenance. `registered: false` marks a DERIVED row — one cluster-api
    // synthesised from an operator-enabled dev-mode record with no
    // `dev_environments` document behind it. Its id is `dm_…`, not `de_…`,
    // and every per-id route (GET / start / stop / DELETE) deliberately 404s
    // on it. `!== false` rather than a truthiness test on purpose: an older
    // API that omits the field must keep behaving exactly as before.
    registered: e.registered !== false,
    ownership: e.ownership || null,
    // Every action is gated on `registered`. Offering a button whose only
    // possible outcome is a 404 is how a surface teaches people to stop
    // trusting it.
    canStart: e.registered !== false && STARTABLE.has(status),
    canStop: e.registered !== false && STOPPABLE.has(status),
    canDelete: e.registered !== false,
    canOpenVscode: e.registered !== false && vscodeLikelyReady,
    // A derived row is promoted through the existing adopt branch of
    // POST /api/cluster/dev-environments — but only when we know which
    // instance to adopt.
    canAdopt: e.registered === false && target.instance_id != null,
    isTransitional: TRANSITIONAL.has(status),
    _raw: e
  }
}

// ── Workspace actions ───────────────────────────────────────────────────────
function vscodeUnavailableHint(reason) {
  if (!reason) return 'VS Code is unavailable for this environment.'
  if (reason === 'dev_workspace_not_requested') {
    return 'Browser VS Code was not requested for this environment'
  }
  if (/^environment_/.test(reason)) return 'Start the environment to open VS Code'
  return String(reason).replace(/_/g, ' ')
}

/**
 * Button state for the two workspace entry points from a `GET /<id>/workspace`
 * payload (`vscode.state` = ready|preparing|unavailable, `shell_state` =
 * ready|preparing|not_requested|unavailable).
 */
export function workspaceActions(ws) {
  const w = ws && typeof ws === 'object' ? ws : {}
  const vs = w.vscode && typeof w.vscode === 'object' ? w.vscode : {}
  const vState = vs.state || 'unavailable'
  let vscode
  if (vState === 'ready') {
    vscode = { enabled: Boolean(vs.url), label: 'Open VS Code', hint: null, state: 'ready', url: vs.url || null }
  } else if (vState === 'preparing') {
    vscode = { enabled: false, label: 'Preparing…', hint: 'Your browser IDE is starting', state: 'preparing', url: null }
  } else {
    vscode = { enabled: false, label: 'Unavailable', hint: vscodeUnavailableHint(vs.reason), state: 'unavailable', url: null }
  }

  const sState = w.shell_state || (w.shell ? 'ready' : 'unavailable')
  let shell
  if (sState === 'ready') {
    shell = { enabled: Boolean(w.shell), label: 'Open shell', hint: null, state: 'ready' }
  } else if (sState === 'preparing') {
    shell = { enabled: false, label: 'Preparing…', hint: 'The development container is starting', state: 'preparing' }
  } else if (sState === 'not_requested') {
    shell = { enabled: false, label: 'Not requested', hint: 'A shell was not requested for this environment', state: 'not_requested' }
  } else {
    shell = { enabled: false, label: 'Unavailable', hint: 'Start the environment to open a shell', state: 'unavailable' }
  }
  return { vscode, shell }
}

/**
 * Copy for a pod-shell socket `exec_error`. A permission refusal from the
 * backend surfaces as a message mentioning forbidden / Admin.
 */
export function shellExecErrorCopy(message) {
  const m = String(message || '')
  if (/forbidden|admin/i.test(m)) {
    return 'Opening a shell needs edit access to this app. Ask your organisation admin.'
  }
  return m || 'The shell could not be opened. Try again in a moment.'
}

// ── Timeline ────────────────────────────────────────────────────────────────
const EVENT_LABELS = {
  requested: 'Requested',
  provisioning: 'Provisioning',
  active: 'Running',
  paused: 'Suspended',
  stopping: 'Stopping',
  stopped: 'Stopped',
  failed: 'Failed'
}

/**
 * Label for a `kind:'git_push'` audit event (developer loop Stage A):
 * "Pushed `<short_sha>` to `<branch>`" — degrades gracefully when either is
 * missing. Exported so the card's success toast and the timeline agree.
 */
export function pushTimelineLabel(event) {
  const ev = event && typeof event === 'object' ? event : {}
  const sha = typeof ev.short_sha === 'string' && ev.short_sha
    ? ev.short_sha
    : typeof ev.sha === 'string' && ev.sha ? ev.sha.slice(0, 8) : null
  const branch = typeof ev.branch === 'string' && ev.branch ? ev.branch : null
  if (sha && branch) return `Pushed ${sha} to ${branch}`
  if (sha) return `Pushed ${sha}`
  if (branch) return `Pushed to ${branch}`
  return 'Pushed'
}

/** Events that carry a `kind` instead of a status transition (`to == null`). */
const KIND_EVENTS = {
  git_push: { label: pushTimelineLabel, tone: 'blue', icon: 'fa-code-commit' }
}

/**
 * `audit.events[]` → timeline entries, NEWEST FIRST (stable for equal
 * timestamps). Each: `{ at, from, to, reason, actor, label, tone, kind, meta }`.
 * Status-transition events label from `to`; `to == null` events label from
 * `kind` (e.g. `git_push` → "Pushed abc12345 to development") and carry a
 * mono `meta` line (the branch / sha) instead of the `from → to` pair.
 */
export function timelineFromAudit(env) {
  const audit = env && env.audit && typeof env.audit === 'object' ? env.audit : {}
  const events = Array.isArray(audit.events) ? audit.events : []
  return events
    .filter((ev) => ev && typeof ev === 'object')
    .map((ev, idx) => {
      const kind = typeof ev.kind === 'string' && ev.kind ? ev.kind : null
      const kindMeta = ev.to == null && kind ? KIND_EVENTS[kind] : null
      let label
      let tone
      let meta = null
      if (kindMeta) {
        label = typeof kindMeta.label === 'function' ? kindMeta.label(ev) : kindMeta.label
        tone = kindMeta.tone
        meta = kind === 'git_push'
          ? [ev.branch, ev.sha ? String(ev.sha).slice(0, 8) : null].filter(Boolean).join(' · ') || null
          : null
      } else if (ev.to == null && kind) {
        label = String(kind).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
        tone = 'grey'
      } else {
        label = EVENT_LABELS[ev.to] || (ev.to ? String(ev.to) : 'Updated')
        tone = statusMeta(ev.to).tone
      }
      return {
        at: ev.at || null,
        from: ev.from || null,
        to: ev.to || null,
        reason: ev.reason || null,
        actor: ev.actor || null,
        kind,
        label,
        tone,
        meta,
        _idx: idx
      }
    })
    .sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0
      const tb = b.at ? new Date(b.at).getTime() : 0
      if (tb !== ta) return tb - ta
      return b._idx - a._idx
    })
    .map(({ _idx, ...rest }) => rest)
}

// ── Node picker (ported from the operator card) ─────────────────────────────
const NODE_RANK = { 'dev-node': 0, worker: 1, tainted: 2, 'control-plane': 3, cordoned: 4 }

/**
 * Rank `/management/nodes` for the node picker. Control-plane nodes are
 * EXCLUDED (a dev pod must never land there); every other node is selectable —
 * the dev pod tolerates the node's taints, the node is never changed. Order:
 * dev-node → plain worker → tainted/special → cordoned (listed last, disabled);
 * alphabetical within a rank.
 * Returns `[{ node, name, kind, disabled, blocking, labels, badges }]`.
 */
export function pickableNodes(all) {
  const out = []
  ;(Array.isArray(all) ? all : []).forEach((n) => {
    if (!n || !n.name) return
    if (n.role === 'control-plane') return
    let blocking = Array.isArray(n.blocking_taints) ? n.blocking_taints : null
    if (blocking === null && Array.isArray(n.taints)) {
      blocking = n.taints.filter(
        (t) => t && (t.effect === 'NoSchedule' || t.effect === 'NoExecute') && t.key !== 'dev-node'
      )
    }
    blocking = blocking || []
    const labels = n.labels_of_interest && typeof n.labels_of_interest === 'object' ? n.labels_of_interest : {}
    const cordoned = n.unschedulable === true || n.schedulable === false
    let kind
    if (cordoned) kind = 'cordoned'
    else if (n.is_dev_node) kind = 'dev-node'
    else if (blocking.length || labels['node-role']) kind = 'tainted'
    else kind = 'worker'
    const badges = []
    if (kind === 'cordoned') badges.push('cordoned')
    if (kind === 'dev-node') badges.push('dev-node')
    blocking.forEach((t) => badges.push(`${t.key || 'taint'} taint · dev pod will tolerate`))
    Object.keys(labels).forEach((k) => {
      if (k === 'node-role' && labels[k] === 'dev-node') return
      badges.push(`${k}: ${labels[k]}`)
    })
    out.push({ node: n, name: n.name, kind, disabled: kind === 'cordoned', blocking, labels, badges })
  })
  out.sort((a, b) => {
    const ra = NODE_RANK[a.kind]
    const rb = NODE_RANK[b.kind]
    if (ra !== rb) return ra - rb
    return String(a.name).localeCompare(String(b.name))
  })
  return out
}

/** Default pick: a dev-node, else the first plain worker, else ''. */
export function defaultNode(entries) {
  const list = Array.isArray(entries) ? entries : []
  const dev = list.find((e) => e.kind === 'dev-node')
  if (dev) return dev.name
  const w = list.find((e) => e.kind === 'worker')
  return w ? w.name : ''
}

// ── Create form ─────────────────────────────────────────────────────────────
/**
 * Validate the create form against the instance's `dev_mode` offer. Returns
 * `{ ok, errors:{field:message}, form }` where `form` is the NORMALISED form:
 * `devWorkspace` is forced to false when the offer says browser VS Code is not
 * available on this server (`offer.dev_workspace_available === false`), and
 * `devRun` likewise when `dev_run_available === false`.
 */
export function validateCreateForm(form, offer) {
  const f = form && typeof form === 'object' ? { ...form } : {}
  const o = offer && typeof offer === 'object' ? offer : {}
  const errors = {}
  if (f.instanceId === undefined || f.instanceId === null || f.instanceId === '') {
    errors.instanceId = 'Choose an app to develop.'
  }
  if (!f.node || !String(f.node).trim()) {
    errors.node = 'Choose a node to run the development pod on.'
  }
  if (!f.container || !String(f.container).trim()) {
    errors.container = 'Choose the container to develop in.'
  }
  if (f.name !== undefined && f.name !== null && String(f.name).length > 80) {
    errors.name = 'Keep the name under 80 characters.'
  }
  if (o.dev_workspace_available === false) f.devWorkspace = false
  if (o.dev_run_available === false) f.devRun = false
  return { ok: Object.keys(errors).length === 0, errors, form: f }
}

/**
 * Build the `POST /cluster/dev-environments` body from a (normalised) form.
 * `{ kind:'cs_application_dev_mode', name?, target:{ instance_id },
 *    enable:{ node, container, git_branch?, dev_workspace, dev_run, resources_increased } }`
 * Optional `offer` re-applies the dev_workspace/dev_run forcing so callers who
 * skip validateCreateForm still cannot request an unavailable feature.
 */
export function buildCreatePayload(form, offer) {
  const f = offer ? validateCreateForm(form, offer).form : (form && typeof form === 'object' ? form : {})
  const enable = {
    node: f.node ? String(f.node).trim() : '',
    container: f.container ? String(f.container).trim() : '',
    dev_workspace: f.devWorkspace === true,
    dev_run: f.devRun === true,
    resources_increased: f.resourcesIncreased === true
  }
  if (f.gitBranch && String(f.gitBranch).trim()) enable.git_branch = String(f.gitBranch).trim()
  const payload = {
    kind: 'cs_application_dev_mode',
    target: { instance_id: f.instanceId },
    enable
  }
  if (f.name && String(f.name).trim()) payload.name = String(f.name).trim()
  return payload
}

/**
 * Seed a create form from the offer's `defaults` block. `containers[]` with a
 * single entry pre-selects it; `dev_workspace`/`dev_run` default to available.
 */
export function formFromOffer(offer, instanceId) {
  const o = offer && typeof offer === 'object' ? offer : {}
  const d = o.defaults && typeof o.defaults === 'object' ? o.defaults : {}
  const containers = Array.isArray(d.containers) ? d.containers : []
  return {
    instanceId: instanceId != null ? instanceId : null,
    name: '',
    node: '',
    container: d.container || (containers.length === 1 ? containers[0] : ''),
    gitBranch: d.git_branch || '',
    devWorkspace: o.dev_workspace_available !== false,
    devRun: o.dev_run_available !== false,
    resourcesIncreased: false
  }
}

// ── One-time password ───────────────────────────────────────────────────────
/**
 * Extract the one-time code-server password from a create/start response's
 * `dev_workspace` block. Returned ONLY for immediate display — never store it
 * (not in Vuex, not in storage). `null` when there is no password to show.
 */
export function oneTimePasswordFrom(createData) {
  const d = createData && typeof createData === 'object' ? createData : {}
  const dw = d.dev_workspace && typeof d.dev_workspace === 'object' ? d.dev_workspace : null
  if (!dw || typeof dw.password !== 'string' || !dw.password) return null
  return {
    url: dw.url || null,
    password: dw.password,
    needsPassword: dw.url_needs_password !== false
  }
}

// ── Polling cadence ─────────────────────────────────────────────────────────
export const POLL_BASE_MS = 3000
export const POLL_FACTOR = 1.5
export const POLL_CAP_MS = 15000

/** Poll the list only while some row is mid-transition. */
export function needsListPoll(rows) {
  return (Array.isArray(rows) ? rows : []).some((r) => r && r.isTransitional === true)
}

/** 3000 → ×1.5 each tick → capped at 15000. `prev` of 0/undefined restarts. */
export function nextPollDelay(prev) {
  if (!prev || prev <= 0) return POLL_BASE_MS
  return Math.min(POLL_CAP_MS, Math.round(prev * POLL_FACTOR))
}

// ── Error copy ──────────────────────────────────────────────────────────────
function fmt(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k]
    return v === undefined || v === null || v === '' ? '' : String(v)
  })
}

const GENERIC_REQUEST_CODES = new Set([
  'kind_target_mismatch', 'invalid_kind', 'invalid_request', 'fields_not_applicable', 'invalid_target'
])

/**
 * Map an API error (thrown axios error, or a bare code string) to customer
 * copy. `ctx` may carry `{ node, status, message }` to fill placeholders.
 * Returns `{ code, title, message, tone:'error'|'warning'|'info', field? }`.
 */
export function errorCopy(err, ctx = {}) {
  let code = null
  let status
  let message = ''
  let extra = null
  if (typeof err === 'string') {
    code = err
  } else if (err) {
    const parsed = apiErrorCode(err)
    code = parsed.code
    status = parsed.status
    message = parsed.message
    extra = parsed.extra
  }
  const c = ctx && typeof ctx === 'object' ? ctx : {}
  const vars = {
    message: c.message || message || 'unknown error',
    node: c.node || (extra && (extra.node || extra.name)) || 'you chose',
    status: c.status || (extra && extra.status) || 'busy',
    owner_user_id: (extra && (extra.owner_user_id != null ? extra.owner_user_id : null)) ?? c.owner_user_id ?? 'unknown'
  }
  const out = (title, template, tone = 'error', field) => {
    const r = { code, title, message: fmt(template, vars), tone }
    if (field) r.field = field
    return r
  }

  switch (code) {
    case 'dev_environments_disabled':
      return out('Not enabled', 'Dev Environments are not enabled on this platform yet. Ask your operator to switch them on.', 'info')
    case 'service_unavailable':
      return out('Permissions service unreachable', 'We could not reach the permissions service. Try again in a moment.', 'warning')
    case 'forbidden': {
      const reason = extra && extra.reason
      if (reason === 'not_owner') return out('Not yours', 'This environment belongs to another developer.')
      if (reason === 'create_not_permitted') {
        return out('Permission needed', 'You cannot create a dev environment on this app yet. An operator or your organisation admin must grant you dev_environment: write on it.')
      }
      return out('Permission needed', 'You need the dev-environment permission. Ask your operator or organisation admin for access.')
    }
    case 'target_busy':
      return out('App in use', 'Another developer (user #{owner_user_id}) already has a live environment on this app. Ask them to stop it, or pick another app.', 'warning')
    case 'already_active':
      return out('Already running', 'Dev mode is already running on this app outside Dev Environments. Refresh — it will be adopted.', 'info')
    case 'third_party_no_repo':
      return out('No source repository', 'This app runs a third-party image with no source repository, so it cannot be developed in place.')
    case 'unresolved_repo':
      return out('Repository not linked', "We could not resolve this app's repository. Link it on the app's GitLab tab first.")
    case 'stateful_clustered':
      return out('Not supported', 'Stateful or clustered components cannot run in dev mode — converting them to a single replica would break the cluster.')
    case 'protected_workload':
      return out('Protected workload', 'This is a protected platform workload. Platform services get their own isolated environments (coming soon).', 'info')
    case 'daemonset_convert':
      return out('Per-node agent', 'This component runs as a per-node agent. Dev mode parks it and runs one development copy; it is restored when you stop.', 'warning')
    case 'target_kind_unsupported':
      return out('Not available yet', 'Catalog components are not available for dev environments yet.', 'info')
    case 'not_yet_available':
      return out('Coming later', 'Platform-service environments arrive in a later release.', 'info')
    case 'target_not_found':
      return out('App missing', 'That app no longer exists. Refresh the list.')
    case 'node_cordoned':
      return out('Node cordoned', 'Node {node} is cordoned. Choose another node.', 'error', 'node')
    case 'node_not_found':
      return out('Node not found', 'Node {node} was not found. Choose another node.', 'error', 'node')
    case 'git_url_unresolved':
      return out('Repository URL missing', 'We could not find a repository URL for this app. Link it on the GitLab tab.')
    case 'unsafe_hostpath':
      return out('Source directory not allowed', 'The source directory the platform chose is not allowed. Contact your operator.')
    case 'dev_workspace_disabled':
      return out('Browser VS Code unavailable', 'Browser VS Code is not enabled on this server. Create the environment without it to get a shell only.', 'warning')
    case 'enable_failed':
      return out('Enabling failed', 'Enabling dev mode failed: {message}. The environment is marked as Error — you can retry with Start.')
    case 'disable_failed':
    case 'live_restore_failed':
      return out('Stopping failed', 'Stopping failed: {message}. Try again or contact your operator.')
    case 'addon_persist_failed':
      return out('Save failed', 'Dev mode was applied but its configuration could not be saved: {message}. Contact your operator.')
    case 'invalid_transition':
      return out('Not right now', 'This environment is {status} and cannot be started right now.', 'warning')
    case 'not_supported_for_kind':
      return out('Not supported', 'That action is not supported for this kind of environment.', 'info')
    case 'not_found':
      return out('Gone', 'This environment no longer exists.')
    case 'env_not_active':
      return out('Not running', 'This is available while the environment is running. Start it and try again.', 'info')
    case 'target_unresolved':
      return out('Target unknown', 'This environment has no cluster or namespace on record. Contact your operator.')
    case 'connection_info_failed':
      return out('Cluster not reachable', 'We could not read the application’s connection details from the cluster. Try again in a moment.', 'warning')
    default:
      break
  }
  if (code && GENERIC_REQUEST_CODES.has(code)) {
    return out('Request not accepted', 'Something in the request was not right ({message}). Please try again.')
  }
  if (status === 502) {
    return out('Enabling failed', 'Enabling dev mode failed: {message}. The environment is marked as Error — you can retry with Start.')
  }
  if (status === 401) {
    return out('Signed out', 'Your session expired. Please sign in again.', 'warning')
  }
  if (status === 403) {
    return out('Permission needed', 'You need the dev-environment permission. Ask your operator or organisation admin for access.')
  }
  if (status === 404) {
    return out('Gone', 'This environment no longer exists.')
  }
  if (status === undefined && err && typeof err !== 'string') {
    return out('Connection problem', 'We could not reach the platform. Check your connection and try again.', 'warning')
  }
  return out('Something went wrong', '{message}')
}

// ── Developer loop Stage A — Changes card ───────────────────────────────────
// Backend: cluster-api routes/dev_environments.py `/git/status`, `/git/diff`,
// `/git/commit-push` (services/dev_workspace_git.py). Porcelain v2 `XY` codes
// arrive already normalised: `.` stripped and the pair trimmed, so ` M` → `M`,
// `A.` → `A`, `MM`/`AM` stay two letters, untracked is `??`.

const FILE_STATUS = Object.freeze({
  '?': { label: 'Untracked', tone: 'grey' },
  M: { label: 'Modified', tone: 'amber' },
  A: { label: 'Added', tone: 'green' },
  D: { label: 'Deleted', tone: 'red' },
  R: { label: 'Renamed', tone: 'blue' },
  C: { label: 'Copied', tone: 'blue' },
  T: { label: 'Type changed', tone: 'amber' },
  U: { label: 'Conflict', tone: 'red' }
})

const CHANGED_CHIP = Object.freeze({ label: 'Changed', tone: 'grey' })

/**
 * `{ label, tone }` for a porcelain status code. Accepts the normalised
 * one/two-letter form the API sends AND raw v1/v2 pairs (`' M'`, `'M.'`,
 * `'.M'`, `'??'`). A pair with two different letters reads from the index
 * side first (`AM` → Added, `MD` → Modified); any `U` (or `AA`/`DD`) wins as
 * Conflict. Unknown → "Changed" (grey) — never throws.
 */
export function fileStatusChip(code) {
  const raw = code == null ? '' : String(code)
  const letters = raw.replace(/[.\s]/g, '')
  if (!letters) return { code: raw, ...CHANGED_CHIP }
  if (letters.startsWith('?')) return { code: raw, ...FILE_STATUS['?'] }
  if (letters.includes('U') || letters === 'AA' || letters === 'DD') return { code: raw, ...FILE_STATUS.U }
  const first = letters[0].toUpperCase()
  const meta = FILE_STATUS[first] || (letters[1] && FILE_STATUS[letters[1].toUpperCase()]) || CHANGED_CHIP
  return { code: raw, ...meta }
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * One-line summary of a `/git/status` payload, e.g.
 * "3 files changed · 1 ahead of origin/development" or
 * "Working tree clean · up to date with origin/development". `null` status →
 * ''. Behind counts are called out because a push would be rejected.
 */
export function changesSummary(status) {
  const s = status && typeof status === 'object' ? status : null
  if (!s) return ''
  const changed = Number.isFinite(Number(s.changed_count))
    ? Number(s.changed_count)
    : Array.isArray(s.changed_files) ? s.changed_files.length : 0
  const clean = s.clean === true || (s.clean == null && changed === 0)
  const parts = [clean ? 'Working tree clean' : `${plural(changed, 'file', 'files')} changed`]
  const ahead = Number(s.ahead) || 0
  const behind = Number(s.behind) || 0
  const upstream = typeof s.upstream === 'string' && s.upstream ? s.upstream : null
  if (s.detached === true) {
    parts.push('detached HEAD')
  } else if (!upstream) {
    parts.push('no upstream yet — the first push sets it')
  } else if (ahead && behind) {
    parts.push(`${ahead} ahead, ${behind} behind ${upstream}`)
  } else if (ahead) {
    parts.push(`${ahead} ahead of ${upstream}`)
  } else if (behind) {
    parts.push(`${behind} behind ${upstream}`)
  } else {
    parts.push(`up to date with ${upstream}`)
  }
  return parts.join(' · ')
}

/** Every `/git/*` machine code the card must have copy for. */
export const GIT_ERROR_CODES = Object.freeze([
  'missing_message', 'invalid_path', 'invalid_branch',
  'env_not_active', 'shell_requires_dev_workspace', 'not_a_repository', 'detached_head',
  'nothing_to_commit', 'no_identity', 'push_rejected', 'busy',
  'workspace_not_ready',
  'push_auth_failed', 'push_failed', 'commit_failed', 'exec_failed',
  'not_owner', 'forbidden', 'not_found'
])

const GIT_COPY = Object.freeze({
  missing_message: { title: 'Message needed', message: 'Write a commit message before pushing.', tone: 'warning', field: 'message' },
  invalid_path: { title: 'Path not accepted', message: 'One of the file paths is outside the repository or looks like a git option. Refresh the list and try again.', tone: 'error' },
  invalid_branch: { title: 'Branch name not accepted', message: 'The branch must be a plain ref name — no spaces, no leading dash.', tone: 'error', field: 'branch' },
  env_not_active: { title: 'Environment not running', message: 'Start the environment to see and push changes.', tone: 'info', action: 'start' },
  shell_requires_dev_workspace: { title: 'Browser workspace needed', message: 'Changes work inside the browser VS Code workspace, which this environment was created without. Recreate it with browser VS Code to commit from here.', tone: 'info' },
  not_a_repository: { title: 'No repository yet', message: 'The workspace folder is not a git repository yet — the clone may still be running. Refresh in a moment.', tone: 'warning', action: 'refresh' },
  detached_head: { title: 'Detached HEAD', message: 'The workspace is not on a branch. Check one out in the VS Code terminal (`git switch <branch>`), then push again.', tone: 'warning' },
  nothing_to_commit: { title: 'Nothing to commit', message: 'The working tree is clean — there are no changes to push.', tone: 'info', action: 'refresh' },
  no_identity: { title: 'Git identity missing', message: 'The workspace has no git identity. Run `git config --global user.email you@example.com` and `git config --global user.name "Your Name"` in the VS Code terminal, then push again.', tone: 'warning' },
  push_rejected: { title: 'Push rejected', message: "The remote has commits you don't have — pull or rebase in the terminal, then push again.", tone: 'warning', action: 'refresh' },
  busy: { title: 'Already running', message: 'Another commit or push is in progress on this environment. Wait for it to finish, then try again.', tone: 'info' },
  workspace_not_ready: { title: 'Workspace starting', message: "The workspace is still starting — Changes will load when it's ready.", tone: 'info', action: 'wait' },
  push_auth_failed: { title: 'Push not authorised', message: 'The remote refused the workspace credentials. The deploy token may have expired — ask your operator to rotate it, then push again.', tone: 'error' },
  push_failed: { title: 'Push failed', message: 'The commit was made but the push did not complete. Check the console below, then push again — nothing was lost.', tone: 'error', action: 'refresh' },
  commit_failed: { title: 'Commit failed', message: 'git could not create the commit. The console below has the reason; a pre-commit hook is the usual cause.', tone: 'error' },
  exec_failed: { title: 'Workspace unreachable', message: 'The command could not run inside the workspace container. Refresh; if it keeps failing, restart the environment.', tone: 'error', action: 'refresh' },
  not_owner: { title: 'Not yours', message: 'This environment belongs to another developer — only its owner can push from it.', tone: 'error' },
  forbidden: { title: 'Permission needed', message: 'Pushing needs dev_environment: write on this app. Ask your operator or organisation admin.', tone: 'error' },
  not_found: { title: 'Gone', message: 'This environment no longer exists.', tone: 'error' }
})

/**
 * Copy for a `/git/*` failure. Accepts a thrown axios error or a bare code
 * string. Returns `{ code, title, message, tone:'error'|'warning'|'info',
 * action?, field?, console? }` — `console` is the scrubbed git output the
 * server attaches on 502s (`errors[0].console`), for a collapsed details block.
 * Every code in GIT_ERROR_CODES has specific copy; unknown codes fall back by
 * HTTP status, then to the server message. Never throws.
 */
export function gitErrorCopy(err) {
  let code = null
  let status
  let message = ''
  let extra = null
  if (typeof err === 'string') {
    code = err
  } else if (err) {
    const parsed = apiErrorCode(err)
    code = parsed.code
    status = parsed.status
    message = parsed.message
    extra = parsed.extra
  }
  if (code === 'forbidden' && extra && extra.reason === 'not_owner') code = 'not_owner'
  const consoleText = extra && typeof extra.console === 'string' && extra.console.trim() ? extra.console : null
  const finish = (base) => {
    const r = { code, title: base.title, message: base.message, tone: base.tone || 'error' }
    if (base.action) r.action = base.action
    if (base.field) r.field = base.field
    if (consoleText) r.console = consoleText
    return r
  }
  if (code && GIT_COPY[code]) return finish(GIT_COPY[code])
  if (status === 401) return finish({ title: 'Signed out', message: 'Your session expired. Sign in again to keep working.', tone: 'warning' })
  if (status === 403) return finish(GIT_COPY.forbidden)
  if (status === 404) return finish(GIT_COPY.not_found)
  if (status === 503) return finish(GIT_COPY.workspace_not_ready)
  if (status === 502) return finish({ title: 'Command failed in the workspace', message: message || 'git did not complete. Check the console below and try again.', tone: 'error' })
  if (status === undefined && err && typeof err !== 'string') {
    return finish({ title: 'Connection problem', message: 'We could not reach the platform. Check your connection and try again.', tone: 'warning' })
  }
  return finish({ title: 'Something went wrong', message: message || 'The request did not complete. Try again.', tone: 'error' })
}

/**
 * Why "Commit & push" is disabled right now, or `null` when it may be
 * pressed. Order matters — the most fundamental blocker wins.
 *   { status, message, busy, active, ready }
 *   status  — the `/git/status` payload (or null while loading)
 *   message — the commit message draft
 *   busy    — a commit/push is in flight
 *   active  — env.status === 'active'
 *   ready   — false while the workspace is still starting (optional)
 */
export function commitDisabledReason({ status, message, busy, active, ready, selected } = {}) {
  if (active === false) return 'Start the environment to push changes.'
  if (ready === false) return 'The workspace is still starting.'
  if (busy) return 'Committing and pushing…'
  if (!status) return 'Loading the working tree…'
  if (status.detached === true) return 'Check out a branch in the terminal first.'
  const changed = Number.isFinite(Number(status.changed_count))
    ? Number(status.changed_count)
    : Array.isArray(status.changed_files) ? status.changed_files.length : 0
  if (status.clean === true || changed === 0) return 'Nothing to commit — the working tree is clean.'
  if (selected === 0) return 'Select at least one file.'
  if (!message || !String(message).trim()) return 'Write a commit message.'
  return null
}

// ── Per-file selection (Changes card polish) ────────────────────────────────

/**
 * The ledger line above the push button: "N of M files will be committed and
 * pushed to <branch>". `M` drives the singular/plural; `tone` is `'amber'`
 * whenever a SUBSET is going out (N < M, including N = 0) and `'grey'` when
 * everything is. Returns `{ text, lead, verb, branch, tone, subset }` — `lead`
 * is the "N of M files" fragment (set bold by the card), `verb` the phrase up
 * to but excluding the branch, `branch` the trimmed ref or null.
 */
export function commitPreview({ selected, total, branch } = {}) {
  const m = Math.max(0, Number(total) || 0)
  const n = Math.min(m, Math.max(0, Number(selected) || 0))
  const lead = `${n} of ${plural(m, 'file', 'files')}`
  const ref = typeof branch === 'string' && branch.trim() ? branch.trim() : null
  const verb = ref ? 'will be committed and pushed to' : 'will be committed and pushed'
  const subset = n < m
  return {
    text: ref ? `${lead} ${verb} ${ref}` : `${lead} ${verb}`,
    lead,
    verb,
    branch: ref,
    tone: subset ? 'amber' : 'grey',
    subset
  }
}

/**
 * Body for `POST /<id>/git/commit-push`. Everything selected → `add_all:true`
 * (keeps `git add -A` semantics, deletions included); a subset → `paths:[…]`
 * (the backend runs `git add -- <paths>`, which also stages untracked files).
 * `selected` is the list of chosen paths; `total` the number of changed files.
 * `branch` is forwarded only when given. Never sends both `paths` and `add_all`.
 */
export function commitPushBody({ message, selected, total, branch } = {}) {
  const paths = (Array.isArray(selected) ? selected : [])
    .filter((p) => typeof p === 'string' && p.trim())
    .map((p) => p.trim())
  const m = Number.isFinite(Number(total)) ? Number(total) : paths.length
  const body = { message: message == null ? '' : String(message).trim() }
  if (paths.length && paths.length < m) body.paths = paths
  else body.add_all = true
  if (typeof branch === 'string' && branch.trim()) body.branch = branch.trim()
  return body
}

export const FRESH_ENV_WINDOW_MS = 10 * MIN

/**
 * True when the environment was created within the last ten minutes (reads
 * `lifecycle.created_at`, falling back to a top-level `created_at`). Used to
 * flag a dirty tree on FIRST load as pre-existing — i.e. stale hostPath edits
 * the developer did not make in this session. Missing/invalid dates → false.
 */
export function isFreshEnv(env, now = Date.now(), windowMs = FRESH_ENV_WINDOW_MS) {
  const e = env && typeof env === 'object' ? env : {}
  const lifecycle = e.lifecycle && typeof e.lifecycle === 'object' ? e.lifecycle : {}
  const iso = lifecycle.created_at || e.created_at || null
  if (!iso) return false
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return false
  return now - t < windowMs
}

/**
 * Display label for a changed-file entry: `orig → new` for renames/copies
 * (`orig_path` or `origPath` differing from `path`), else the path alone.
 */
export function renameLabel(file) {
  const f = file && typeof file === 'object' ? file : {}
  const path = f.path == null ? '' : String(f.path)
  const orig = f.orig_path != null ? String(f.orig_path) : f.origPath != null ? String(f.origPath) : ''
  if (orig && orig !== path) return `${orig} → ${path}`
  return path
}

/** Rail / overall tone for the card edge from a status + error. */
export function changesTone({ status, error, justPushed } = {}) {
  if (error && error.tone === 'error') return 'red'
  if (justPushed) return 'green'
  if (!status) return 'grey'
  if (status.detached === true) return 'amber'
  const changed = Number.isFinite(Number(status.changed_count))
    ? Number(status.changed_count)
    : Array.isArray(status.changed_files) ? status.changed_files.length : 0
  if (status.clean === false || changed > 0) return 'amber'
  if ((Number(status.ahead) || 0) > 0) return 'blue'
  return 'grey'
}

// ── Catalog components in the create wizard (§21.1 Stage 2/3) ───────────────
// Backend: cluster-api routes/uts_instance_detail_page.py
// `_catalog_dev_mode_offer` (offer with `kind:'catalog'` + `containers[]`
// candidates from the Infra App Sources registry, each with an eligibility
// verdict + its own `dev_command`), services/uts/catalog_dev_source.py, and
// routes/dev_environments.py create (`kind:'catalog_component_dev_mode'`,
// `enable.container` REQUIRED → 400 `container_required` with
// `errors[0].containers`). Everything here is ADDITIVE to the Stage 1 helpers
// above — cs-application flows are untouched.

export const KIND_CS_APPLICATION = 'cs_application_dev_mode'
export const KIND_CATALOG_COMPONENT = 'catalog_component_dev_mode'

/**
 * The create-body `kind` for an instance: a cs-application (marketplace
 * cs-application template, denv-standard or GitLab import — see
 * `isCsApplication`) → `cs_application_dev_mode`; everything else is a
 * catalog component → `catalog_component_dev_mode`. Accepts the raw instance
 * doc or a My Apps row (`templateId` / `appType`).
 */
export function kindForInstance(instance) {
  const inst = instance && typeof instance === 'object' ? instance : {}
  const raw = inst._raw && typeof inst._raw === 'object' ? inst._raw : inst
  if (isCsApplication(raw)) return KIND_CS_APPLICATION
  const lower = (v) => String(v || '').trim().toLowerCase()
  if (lower(inst.templateId) === 'cs-application' || lower(inst.appType) === 'cs-application') return KIND_CS_APPLICATION
  return KIND_CATALOG_COMPONENT
}

/** True when the offer is the catalog shape (`kind:'catalog'` or a `containers[]` candidate list). */
export function isCatalogOffer(offer) {
  const o = offer && typeof offer === 'object' ? offer : null
  if (!o) return false
  if (o.kind === 'catalog') return true
  return o.kind !== 'cs-application' && Array.isArray(o.containers)
}

/**
 * Candidate rows of an offer, normalised. Catalog offers carry
 * `containers[{name, eligible, reason_code, reason, git_url, git_branch,
 * workload_kind, dev_command, dev_command_source}]`; a cs-application offer
 * only has `defaults.containers[]` names — every one is eligible and shares
 * the offer-level defaults (repo, branch, dev command).
 */
export function offerCandidates(offer) {
  const o = offer && typeof offer === 'object' ? offer : {}
  const d = o.defaults && typeof o.defaults === 'object' ? o.defaults : {}
  if (Array.isArray(o.containers)) {
    return o.containers
      .filter((c) => c && typeof c === 'object' && c.name)
      .map((c) => ({
        name: String(c.name),
        eligible: c.eligible !== false,
        reason_code: c.reason_code || null,
        reason: c.reason || null,
        git_url: c.git_url || null,
        git_branch: c.git_branch || null,
        workload_kind: c.workload_kind || o.workload_kind || null,
        dev_command: c.dev_command || null,
        dev_command_source: c.dev_command_source || (c.dev_command ? 'template' : 'default')
      }))
  }
  const names = Array.isArray(d.containers) ? d.containers : (d.container ? [d.container] : [])
  return names.filter(Boolean).map((name) => ({
    name: String(name),
    eligible: true,
    reason_code: null,
    reason: null,
    git_url: d.git_url || null,
    git_branch: d.git_branch || null,
    workload_kind: o.workload_kind || null,
    dev_command: d.dev_command || null,
    dev_command_source: d.dev_command_source || (d.dev_command ? 'template' : 'default')
  }))
}

/** Only the candidates that can be developed (`eligible !== false`). */
export function eligibleContainers(offer) {
  return offerCandidates(offer).filter((c) => c.eligible)
}

const DAEMONSET_OPTION_REASON = 'Runs as a DaemonSet — one dev copy runs as a Deployment while active'

/**
 * `<select>` / radio options for the Container field:
 * `[{ value, label, disabled, reason, reason_code, tone }]`. Eligible rows are
 * selectable with `reason:null`; `daemonset_convert` rows stay selectable but
 * carry a `warning` tone + a short reason; ineligible rows are disabled with
 * their `errorCopy` title as the reason (falls back to the server `reason`).
 * Eligible rows come first, then ineligible, each block alphabetical.
 */
export function containerOptions(offer) {
  const rows = offerCandidates(offer).map((c) => {
    if (!c.eligible) {
      const copy = errorCopy(c.reason_code || 'invalid_target', { message: c.reason || '' })
      const reason = c.reason_code ? copy.title : (c.reason || 'Not available')
      return { value: c.name, label: c.name, disabled: true, reason, reason_code: c.reason_code, tone: 'muted' }
    }
    if (c.reason_code === 'daemonset_convert' || c.workload_kind === 'DaemonSet') {
      return { value: c.name, label: c.name, disabled: false, reason: DAEMONSET_OPTION_REASON, reason_code: 'daemonset_convert', tone: 'warning' }
    }
    return { value: c.name, label: c.name, disabled: false, reason: null, reason_code: null, tone: 'ok' }
  })
  return rows.sort((a, b) => {
    if (a.disabled !== b.disabled) return a.disabled ? 1 : -1
    return a.label.localeCompare(b.label)
  })
}

/** The candidate row for a container name (or the first eligible one when omitted). `null` when unknown. */
export function containerCandidate(offer, container) {
  const rows = offerCandidates(offer)
  if (container) return rows.find((c) => c.name === String(container)) || null
  return rows.find((c) => c.eligible) || null
}

/**
 * What "Run the dev server" will execute for a container:
 * `{ command, source:'template'|'default'|'request', line }` where `line` is
 * the review copy — "npm run dev (template)" — or, with no resolved command,
 * "language default (resolved at start)". `null` when the offer is absent.
 * Catalog offers read the chosen candidate; cs-application offers read
 * `defaults.dev_command` (the same for every container).
 */
export function devCommandLine(offer, container) {
  const o = offer && typeof offer === 'object' ? offer : null
  if (!o) return null
  const d = o.defaults && typeof o.defaults === 'object' ? o.defaults : {}
  const cand = containerCandidate(o, container)
  const command = (cand && cand.dev_command) || d.dev_command || null
  let source = (cand && cand.dev_command && cand.dev_command_source) || d.dev_command_source || 'default'
  if (!['template', 'default', 'request'].includes(source)) source = 'default'
  const line = command ? `${command} (${source})` : 'language default (resolved at start)'
  return { command, source, line }
}

/**
 * Default environment name: catalog → "<instance name> · <container> dev";
 * cs-application → "<workload_name | instance name> dev" (Stage 1 behaviour).
 */
export function defaultEnvName(instance, offer, container) {
  const inst = instance && typeof instance === 'object' ? instance : {}
  const name = inst.name || (inst._raw && inst._raw.name) || 'app'
  if (kindForInstance(inst) === KIND_CATALOG_COMPONENT) {
    return container ? `${name} · ${container} dev` : `${name} dev`
  }
  const d = offer && offer.defaults && typeof offer.defaults === 'object' ? offer.defaults : {}
  return `${d.workload_name || name} dev`
}

/**
 * The create body for ANY instance kind: `buildCreatePayload` (Stage 1, always
 * sends `enable.container`) with `kind` taken from the instance —
 * `catalog_component_dev_mode` for catalog components. When the instance is
 * omitted the kind is inferred from the offer (`kind:'catalog'`).
 */
export function buildCreatePayloadFor(form, offer, instance) {
  const payload = buildCreatePayload(form, offer)
  if (instance) payload.kind = kindForInstance(instance)
  else if (isCatalogOffer(offer)) payload.kind = KIND_CATALOG_COMPONENT
  return payload
}

/**
 * Wizard-side copy for a create failure. Adds the Stage 2 `container_required`
 * (400) case — `field:'container'` so the wizard jumps back to the picker, and
 * `containers` echoed from `errors[0].containers` — then delegates to
 * `errorCopy` for everything else.
 */
export function createErrorCopy(err, ctx = {}) {
  const parsed = typeof err === 'string' ? { code: err, extra: null } : (err ? apiErrorCode(err) : { code: null, extra: null })
  if (parsed.code === 'container_required') {
    const containers = parsed.extra && Array.isArray(parsed.extra.containers) ? parsed.extra.containers : []
    const names = containers.filter((c) => c && c.eligible !== false && c.name).map((c) => c.name)
    const hint = names.length ? ` Choose one of: ${names.join(', ')}.` : ''
    return {
      code: 'container_required',
      title: 'Container needed',
      message: `Pick the container to develop in.${hint}`,
      tone: 'error',
      field: 'container',
      containers
    }
  }
  return errorCopy(err, ctx)
}

// ── Developer loop Stage B — Release card ───────────────────────────────────
// Backend: cluster-api routes/dev_environments.py `/release/status`,
// `/release/adopt`, `/release/finalize` (services/dev_environment_release.py is
// the pure derivation; DevEnvironmentService.release_* the I/O). The status
// payload already carries the three `steps[]`; the helpers below turn it into
// rows, a headline, a rail tone, the adopt/finalise gates and the poll rule.
// Nothing here reads the workspace — the endpoint is safe to poll.

export const RELEASE_STEP_KEYS = Object.freeze(['pushed', 'pipeline', 'deployed'])
const RELEASE_STEP_LABELS = Object.freeze({ pushed: 'Pushed', pipeline: 'Pipeline', deployed: 'Deployed' })
const RELEASE_STEP_STATES = new Set(['done', 'running', 'failed', 'pending', 'skipped'])
/** `deployed.state` values after which the deploy side has nothing left to learn. */
export const RELEASE_TERMINAL_STATES = Object.freeze(['deployed', 'failed', 'not_triggered'])
/**
 * GitLab pipeline statuses after which the pipeline side has nothing left to
 * learn. `manual` counts: a blocked manual job waits for a human, not a poll.
 */
export const PIPELINE_TERMINAL_STATUSES = Object.freeze(['success', 'failed', 'canceled', 'cancelled', 'skipped', 'manual'])

export const RELEASE_POLL_BASE_MS = 6000
export const RELEASE_POLL_MAX_MS = 10 * MIN

const PIPELINE_ACTIVE = new Set(['created', 'waiting_for_resource', 'preparing', 'pending', 'running', 'manual', 'scheduled'])
const PIPELINE_FAILED = new Set(['failed', 'canceled', 'cancelled', 'skipped'])

function relObj(v) { return v && typeof v === 'object' ? v : null }

/**
 * True when the pipeline has nothing more to report: there is no pipeline
 * (`null` — CI not triggered, or GitLab unreachable), its status is missing
 * (nothing a poll could learn), or it is terminal (`PIPELINE_TERMINAL_STATUSES`).
 */
export function pipelineSettled(status) {
  const s = relObj(status)
  const pipeline = s ? relObj(s.pipeline) : null
  if (!pipeline) return true
  if (typeof pipeline.status !== 'string' || !pipeline.status) return true
  return PIPELINE_TERMINAL_STATUSES.includes(pipeline.status)
}

/** True when the status (or env) describes a catalog component release. */
export function isCatalogRelease(status, env) {
  const s = relObj(status)
  if (s && typeof s.kind === 'string') return s.kind === 'catalog'
  const e = relObj(env)
  return Boolean(e && e.kind === KIND_CATALOG_COMPONENT)
}

/**
 * The pipeline status that gates a catalog finalise: the matched `pipeline`
 * when it IS the pending build (same id), else the pending block's
 * trigger-time status, else the matched pipeline's status. `null` when unknown.
 * Mirrors `derive_deployed_catalog` server-side.
 */
export function pendingBuildStatus(status) {
  const s = relObj(status)
  if (!s) return null
  const pending = relObj(relObj(s.release) && s.release.pending)
  const pipeline = relObj(s.pipeline)
  if (pending && pipeline && pending.pipeline_id != null && String(pipeline.id) === String(pending.pipeline_id)) {
    return pipeline.status || null
  }
  if (pending && pending.status) return pending.status
  return pipeline ? (pipeline.status || null) : null
}

/**
 * Release poll cadence: 6 s, then the shared ×1.5 back-off to the 15 s cap
 * (`nextPollDelay` floor-clamped to 6 s). `prev` of 0/undefined restarts.
 */
export function releasePollDelay(prev) {
  return Math.max(RELEASE_POLL_BASE_MS, nextPollDelay(prev))
}

/**
 * Stop polling only when BOTH sides are settled: `deployed.state` is terminal
 * (`deployed` / `failed` / `not_triggered`) AND the pipeline is settled
 * (`pipelineSettled`). The live image swaps inside the deploy job before
 * GitLab flips the pipeline to `success`, so a `deployed` verdict with a
 * still-`running` pipeline must keep polling — otherwise the Pipeline row
 * freezes on a stale "running". A missing status keeps polling (the store
 * decides on errors); `unknown` and `pending` keep polling.
 */
export function releasePollShouldStop(status) {
  const s = relObj(status)
  const state = s && relObj(s.deployed) ? s.deployed.state : null
  return RELEASE_TERMINAL_STATES.includes(state) && pipelineSettled(s)
}

/**
 * Always three rows, in order — `{ key, label, state, at, detail, link }`.
 * Reads `status.steps[]` from the server (unknown states → `pending`), fills
 * any row the server omitted as `pending`, and attaches the pipeline
 * `web_url` as `link` on the pipeline row. `null` status → three pending rows.
 *
 * Deployed-first ordering: when `deployed.state` is `deployed` but the
 * pipeline has not settled, the Pipeline row reads `running` with
 * "pipeline #N finishing…" (the image is live; GitLab is wrapping up). Once
 * it settles it becomes `done` (stamped with the pipeline's `updated_at` when
 * the server gave no `at`), or `failed` when a post-deploy job broke — the
 * Deployed row stays `done` either way.
 */
export function releaseSteps(status) {
  const s = relObj(status)
  const byKey = {}
  const list = s && Array.isArray(s.steps) ? s.steps : []
  list.forEach((st) => {
    if (st && typeof st === 'object' && RELEASE_STEP_KEYS.includes(st.key)) byKey[st.key] = st
  })
  const pipeline = s ? relObj(s.pipeline) : null
  const deployedLive = Boolean(s && relObj(s.deployed) && s.deployed.state === 'deployed')
  return RELEASE_STEP_KEYS.map((key) => {
    const st = byKey[key] || {}
    const state = RELEASE_STEP_STATES.has(st.state) ? st.state : 'pending'
    const row = {
      key,
      label: RELEASE_STEP_LABELS[key],
      state,
      at: typeof st.at === 'string' && st.at ? st.at : null,
      detail: typeof st.detail === 'string' && st.detail ? st.detail : null,
      link: null
    }
    if (key === 'pipeline' && pipeline) {
      if (typeof pipeline.web_url === 'string' && pipeline.web_url) row.link = pipeline.web_url
      if (deployedLive && typeof pipeline.status === 'string' && pipeline.status) {
        const num = pipeline.id != null ? ` #${pipeline.id}` : ''
        const stamp = typeof pipeline.updated_at === 'string' && pipeline.updated_at ? pipeline.updated_at : null
        if (PIPELINE_FAILED.has(pipeline.status)) {
          row.state = 'failed'
          row.detail = `pipeline${num} ${pipeline.status}`
          row.at = row.at || stamp
        } else if (pipeline.status === 'success') {
          row.state = 'done'
          row.detail = row.detail || `pipeline${num} success`
          row.at = row.at || stamp
        } else if (!PIPELINE_TERMINAL_STATUSES.includes(pipeline.status)) {
          row.state = 'running'
          row.detail = `pipeline${num} finishing…`
        }
      }
    }
    return row
  })
}

/**
 * The one-line verdict above the timeline.
 *   deployed      → "Deployed ✓ abc12345 on development" (catalog: the tag);
 *                   stays "Deployed ✓ …" while the pipeline is still finishing,
 *                   and gains " (pipeline reported a failure — check the job)"
 *                   when a post-deploy job failed after the image went live
 *   failed        → "Deploy failed" (catalog: "Build failed")
 *   not_triggered → cs-app "CI only runs on development/stage/main";
 *                   catalog "Pushed — build a tag to release it"
 *   pending       → "Building…" while the pipeline runs, "Waiting for the
 *                   pipeline…" before GitLab creates one, "Waiting for deploy…"
 *                   after it succeeds (catalog: "Built — ready to finalise")
 *   unknown       → "Status unknown"
 *   null          → "No release yet"
 */
export function releaseHeadline(status) {
  const s = relObj(status)
  if (!s) return 'No release yet'
  const catalog = isCatalogRelease(s)
  const deployed = relObj(s.deployed) || {}
  const state = deployed.state
  const steps = releaseSteps(s)
  const pipe = steps[1].state
  const sha = (typeof s.short_sha === 'string' && s.short_sha) || (typeof s.sha === 'string' && s.sha ? s.sha.slice(0, 8) : null)
  const branch = typeof s.branch === 'string' && s.branch ? s.branch : null
  if (state === 'deployed') {
    const release = relObj(s.release) || {}
    const ref = catalog ? (release.adopted_tag || deployed.live_ref || sha) : sha
    const what = ref ? ` ${ref}` : ''
    const base = branch ? `Deployed ✓${what} on ${branch}` : `Deployed ✓${what}`
    return pipe === 'failed' ? `${base} (pipeline reported a failure — check the job)` : base
  }
  if (state === 'failed') return catalog && pipe === 'failed' ? 'Build failed' : 'Deploy failed'
  if (state === 'not_triggered') return catalog ? 'Pushed — build a tag to release it' : 'CI only runs on development/stage/main'
  if (state === 'unknown') return 'Status unknown'
  // pending (or anything unexpected)
  if (pipe === 'running') return 'Building…'
  if (pipe === 'done') return catalog && relObj(relObj(s.release) && s.release.pending) ? 'Built — ready to finalise' : 'Waiting for deploy…'
  if (pipe === 'failed') return catalog ? 'Build failed' : 'Deploy failed'
  return 'Waiting for the pipeline…'
}

/**
 * Rail tone for the card edge: green deployed · red failed · amber built-but-
 * not-rolled-out (`not_triggered`) · blue while anything is in flight · grey
 * when nothing is pushed / unknown. A hard error forces red.
 */
export function releaseTone({ status, error } = {}) {
  if (error && error.tone === 'error' && error.code !== 'nothing_pushed') return 'red'
  const s = relObj(status)
  if (!s) return 'grey'
  const state = relObj(s.deployed) ? s.deployed.state : null
  if (state === 'deployed') return 'green'
  if (state === 'failed') return 'red'
  if (state === 'not_triggered') return 'amber'
  if (state === 'pending') return 'blue'
  return 'grey'
}

/** The amber warning under the timeline when a deploy replaces the running dev copy; `null` otherwise. */
export function restartWarning(status) {
  const s = relObj(status)
  if (!s || s.deploy_restarts_environment !== true) return null
  return 'Deploying replaces the running copy — your environment restarts and the workspace reconnects.'
}

/**
 * Whether the "Adopt tag" section shows and whether Build & adopt may be
 * pressed: `{ show, disabled, reason }`. Catalog components only — a
 * cs-application never sees it (apps deploy on push). Disabled while the
 * pending build is still running, or while a request is in flight.
 */
export function adoptAvailability(env, status, { busy = false } = {}) {
  const catalog = isCatalogRelease(status, env)
  if (!catalog) return { show: false, disabled: true, reason: 'Apps deploy on push — nothing to adopt.' }
  if (busy) return { show: true, disabled: true, reason: 'Working…' }
  const s = relObj(status)
  const pending = s ? relObj(relObj(s.release) && s.release.pending) : null
  if (pending) {
    const st = pendingBuildStatus(s)
    if (PIPELINE_ACTIVE.has(st)) return { show: true, disabled: true, reason: `A build for ${pending.tag || 'the pending tag'} is still running — wait for it or finalise it.` }
  }
  return { show: true, disabled: false, reason: null }
}

/**
 * Whether Finalise shows and may be pressed: `{ show, disabled, reason,
 * tag, buildStatus }`. Shows only while `release.pending` exists (catalog).
 * Enabled when the build succeeded — or when its status is unreadable (the
 * server lets an unknown status through); disabled while it runs or after it
 * failed.
 */
export function finaliseAvailability(status, { busy = false } = {}) {
  const s = relObj(status)
  const pending = s ? relObj(relObj(s.release) && s.release.pending) : null
  if (!s || !isCatalogRelease(s) || !pending) return { show: false, disabled: true, reason: null, tag: null, buildStatus: null }
  const tag = pending.tag || null
  const buildStatus = pendingBuildStatus(s)
  if (busy) return { show: true, disabled: true, reason: 'Working…', tag, buildStatus }
  if (PIPELINE_ACTIVE.has(buildStatus)) return { show: true, disabled: true, reason: `Building ${tag || ''}`.trim() + ' — Finalise unlocks when the pipeline succeeds.', tag, buildStatus }
  if (PIPELINE_FAILED.has(buildStatus)) return { show: true, disabled: true, reason: `The build for ${tag || 'this tag'} ${buildStatus} — fix it and build again.`, tag, buildStatus }
  return { show: true, disabled: false, reason: null, tag, buildStatus }
}

/** Every `/release/*` machine code the card must have copy for. */
export const RELEASE_ERROR_CODES = Object.freeze([
  'nothing_pushed', 'invalid_sha',
  'not_applicable_for_kind', 'build_running', 'build_failed', 'nothing_pending',
  'source_unresolved', 'source_not_registered', 'invalid_tag',
  'build_trigger_failed', 'register_failed',
  'not_owner', 'forbidden', 'not_found'
])

const RELEASE_COPY = Object.freeze({
  nothing_pushed: { title: 'Nothing pushed yet', message: 'Push from the Changes card to start a release.', tone: 'info', quiet: true },
  invalid_sha: { title: 'Commit not recognised', message: 'The commit id must be 7–40 hex characters. Refresh to read the last push instead.', tone: 'error', action: 'refresh' },
  not_applicable_for_kind: { title: 'Nothing to adopt', message: 'Apps deploy on push — this SHA is your release. There is no tag to build or adopt.', tone: 'info' },
  build_running: { title: 'Build still running', message: 'The pending build has not finished. Wait for the pipeline to succeed, then finalise.', tone: 'info', action: 'refresh' },
  build_failed: { title: 'Build failed', message: 'The pipeline did not succeed, so nothing was registered. Check the pipeline, fix the build and run Build & adopt again.', tone: 'error', action: 'refresh' },
  nothing_pending: { title: 'No build pending', message: 'There is no tag waiting to be finalised. Run Build & adopt first.', tone: 'info' },
  source_unresolved: { title: 'Component source unknown', message: 'This environment has no template or container recorded, so its source cannot be resolved. Recreate the environment from the app.', tone: 'error' },
  source_not_registered: { title: 'Source not registered', message: 'This component has no entry in the catalog source registry (infra-app-sources.json). Ask your operator to register it.', tone: 'error' },
  invalid_tag: { title: 'Tag not accepted', message: 'Use a plain image tag — no spaces, no leading dash, at most 128 characters.', tone: 'warning', field: 'tag' },
  build_trigger_failed: { title: 'Could not start the build', message: 'GitLab did not accept the pipeline trigger. Check the source project is reachable, then try again.', tone: 'error', action: 'refresh' },
  register_failed: { title: 'Could not register the tag', message: 'The build succeeded but the tag could not be written to the registry entry. Try Finalise again; if it keeps failing, contact your operator.', tone: 'error', action: 'refresh' },
  not_owner: { title: 'Not yours', message: 'This environment belongs to another developer — only its owner can release from it.', tone: 'error' },
  forbidden: { title: 'Permission needed', message: 'Releasing needs dev_environment: write on this app. Ask your operator or organisation admin.', tone: 'error' },
  not_found: { title: 'Gone', message: 'This environment no longer exists.', tone: 'error' }
})

/**
 * Copy for a `/release/*` failure. Accepts a thrown axios error or a bare code
 * string → `{ code, title, message, tone, action?, field?, quiet? }`.
 * `quiet:true` marks the empty state (`nothing_pushed`) that the card renders
 * as a plain line, not a banner. Every code in RELEASE_ERROR_CODES has
 * specific copy; unknown codes fall back by HTTP status, then to the server
 * message. Never throws.
 */
export function releaseErrorCopy(err) {
  let code = null
  let status
  let message = ''
  let extra = null
  if (typeof err === 'string') {
    code = err
  } else if (err) {
    const parsed = apiErrorCode(err)
    code = parsed.code
    status = parsed.status
    message = parsed.message
    extra = parsed.extra
  }
  if (code === 'forbidden' && extra && extra.reason === 'not_owner') code = 'not_owner'
  const finish = (base) => {
    const r = { code, title: base.title, message: base.message, tone: base.tone || 'error' }
    if (base.action) r.action = base.action
    if (base.field) r.field = base.field
    if (base.quiet) r.quiet = true
    return r
  }
  if (code && RELEASE_COPY[code]) return finish(RELEASE_COPY[code])
  if (status === 401) return finish({ title: 'Signed out', message: 'Your session expired. Sign in again to keep working.', tone: 'warning' })
  if (status === 403) return finish(RELEASE_COPY.forbidden)
  if (status === 404) return finish(RELEASE_COPY.not_found)
  if (status === 502) return finish({ title: 'Upstream failed', message: message || 'GitLab or the cluster did not answer. Try again in a moment.', tone: 'error', action: 'refresh' })
  if (status === 503) return finish({ title: 'Service unavailable', message: 'The platform is busy. Try again in a moment.', tone: 'warning', action: 'refresh' })
  if (status === undefined && err && typeof err !== 'string') {
    return finish({ title: 'Connection problem', message: 'We could not reach the platform. Check your connection and try again.', tone: 'warning', action: 'refresh' })
  }
  return finish({ title: 'Something went wrong', message: message || 'The request did not complete. Try again.', tone: 'error' })
}

/** True when the failure is the quiet "nothing pushed" empty state. */
export function isNothingPushed(error) {
  return Boolean(error && error.code === 'nothing_pushed')
}

/** `git.last_push.sha` from an env doc, or `null` — the card loads on mount only when this is set. */
export function lastPushSha(env) {
  const e = relObj(env)
  const last = e && relObj(e.git) ? relObj(e.git.last_push) : null
  return last && typeof last.sha === 'string' && last.sha ? last.sha : null
}

// ── App card — the app's addresses + Connection info (2026-09-06) ────────────
// Backend: cluster-api `workspace.app = { url, urls[] }` (services/
// dev_environment_app_urls.py) and `GET /<id>/connection-info`, which returns
// routes/templates.build_installed_app_connection_info VERBATIM — a dict of
// per-template sections whose keys vary by app (cs_application, grafana,
// web_ui, kafka, credentials, dns_configuration, services, ingresses…). The
// helpers below turn both into stable view rows without knowing every template.

export const APP_URL_KIND_META = Object.freeze({
  dev_server: { label: 'Dev server', tone: 'blue', icon: 'fa-bolt', order: 0 },
  ingress: { label: 'Ingress', tone: 'grey', icon: 'fa-globe', order: 1 },
  loadbalancer: { label: 'LoadBalancer', tone: 'grey', icon: 'fa-network-wired', order: 2 },
  nodeport: { label: 'NodePort', tone: 'grey', icon: 'fa-ethernet', order: 3 }
})
const APP_URL_UNKNOWN_KIND = Object.freeze({ label: 'Link', tone: 'grey', icon: 'fa-link', order: 9 })
export const DEV_SERVER_SUBTITLE = 'Hot-reloading dev server behind the platform auth gate'

function urlKey(url) {
  const u = String(url || '').trim()
  return u.replace(/\/+$/, '').toLowerCase()
}

/**
 * The App card rows from `workspace.app` — `{ url, urls[] }`, the legacy
 * `{ url }`, a bare URL string, or null. Dev server first (synthesised from
 * `url` when the server sent no `urls`), then ingress → LoadBalancer →
 * NodePort → unknown kinds, stable within a kind; duplicates (same URL modulo
 * trailing slash) collapse to the first occurrence. Each row:
 * `{ key, kind, chip:{label,tone,icon}, url, host, port, path, title,
 *    subtitle, servesDevCopy: true|false|null, isDevServer }`.
 * `subtitle` is the server's `note` when present, the dev-server default for
 * the dev server, else the server `label` when it adds something the URL does
 * not already say. Never throws; `[]` for nothing.
 */
export function appUrlRows(app) {
  let url = null
  let list = []
  if (typeof app === 'string') url = app
  else if (app && typeof app === 'object') {
    url = typeof app.url === 'string' ? app.url : null
    list = Array.isArray(app.urls) ? app.urls : []
  }
  const entries = list
    .filter((e) => e && typeof e === 'object' && typeof e.url === 'string' && e.url.trim())
    .map((e, i) => ({ ...e, _i: i }))
  if (url && !entries.some((e) => e.kind === 'dev_server')) {
    entries.unshift({ kind: 'dev_server', url, label: 'Dev server · hot reload', serves_dev_copy: true, _i: -1 })
  }
  const seen = new Set()
  const rows = []
  entries.forEach((e) => {
    const k = urlKey(e.url)
    if (!k || seen.has(k)) return
    seen.add(k)
    const kind = typeof e.kind === 'string' ? e.kind : 'link'
    const meta = APP_URL_KIND_META[kind] || APP_URL_UNKNOWN_KIND
    const isDevServer = kind === 'dev_server'
    const label = typeof e.label === 'string' ? e.label.trim() : ''
    let subtitle = typeof e.note === 'string' && e.note.trim() ? e.note.trim() : ''
    if (!subtitle && isDevServer) subtitle = DEV_SERVER_SUBTITLE
    if (!subtitle && label && !isDevServer && !e.url.includes(label)) subtitle = label
    const serves = e.serves_dev_copy === true ? true : e.serves_dev_copy === false ? false : null
    rows.push({
      key: `${kind}:${k}`,
      kind,
      chip: { label: meta.label, tone: meta.tone, icon: meta.icon },
      url: e.url.trim(),
      host: typeof e.host === 'string' ? e.host : null,
      port: e.port != null && e.port !== '' ? e.port : null,
      path: typeof e.path === 'string' ? e.path : null,
      title: isDevServer ? 'Dev server' : (label || meta.label),
      subtitle,
      servesDevCopy: serves,
      isDevServer,
      _order: meta.order,
      _i: e._i
    })
  })
  rows.sort((a, b) => (a._order - b._order) || (a._i - b._i))
  return rows.map(({ _order, _i, ...r }) => r)
}

// Connection-info normalisation ───────────────────────────────────────────────

const CI_META_KEYS = new Set(['connectivity_ok', 'connectivity_status', 'pods', 'message', '_component_vips', 'deployment_name', MASKING_KEY])
// Per-section masking bookkeeping the server adds (`masked`, `masked_keys`).
// It is a report ABOUT the section, never a connection detail — it drives the
// section badge and is never rendered as a value row.
const MASK_BOOKKEEPING = new Set(SECTION_MASK_KEYS)
const maskedKeyCount = (obj) => (obj && typeof obj === 'object' && Array.isArray(obj.masked_keys) ? obj.masked_keys.length : 0)
const sectionIsMasked = (obj) => Boolean(obj && typeof obj === 'object' && obj.masked === true)
const CI_TITLES = Object.freeze({
  cs_application: 'Application',
  web_ui: 'Web UI',
  kafka_ui: 'Kafka UI',
  kafka_connect: 'Kafka Connect',
  schema_registry: 'Schema registry',
  rabbitmq_amqp: 'RabbitMQ AMQP',
  rabbitmq_management: 'RabbitMQ management',
  dns_configuration: 'DNS configuration',
  dns_instructions: 'DNS instructions',
  metallb: 'MetalLB',
  mongodb: 'MongoDB',
  mysql: 'MySQL',
  postgresql: 'PostgreSQL',
  lan_access: 'LAN access',
  external_access: 'External access',
  quick_start: 'Quick start',
  ingress_controller: 'Ingress controller',
  stats_page: 'Stats page',
  manager_ui: 'Manager UI',
  network_services: 'Network services',
  syslog_web_viewer: 'Syslog web viewer',
  rsyslog_server: 'rsyslog server',
  send_logs_examples: 'Send logs — examples',
  setup_instructions: 'Setup instructions',
  device_adoption: 'Device adoption',
  vip_address: 'VIP address',
  credentials: 'Credentials',
  services: 'Services',
  ingresses: 'Ingress'
})
// Sections the modal shows first, in this order; everything else follows in
// payload order; `credentials` always last.
const CI_ORDER = ['overview', 'cs_application', 'ingresses', 'services', 'dns_configuration']
const SECRET_KEY_RE = /(password|passwd|secret|token|api[_-]?key|private[_-]?key|credential|auth_?key|access[_-]?key|sasl|jaas)/i
const URL_WITH_CREDS_RE = /^[a-z][a-z0-9+.-]*:\/\/[^/@\s]+:[^/@\s]+@/i
const URL_RE = /^[a-z][a-z0-9+.-]*:\/\/\S+$/i

/** `snake_case_key` → "Snake case key" unless `CI_TITLES` knows better. */
export function connectionInfoTitle(key) {
  const k = String(key || '')
  if (CI_TITLES[k]) return CI_TITLES[k]
  const words = k.replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Details'
}

/** True when the key or the value marks a secret (never widen to `username`). */
export function isSecretField(key, value, parentKey) {
  const k = String(key || '')
  if (typeof value === 'string' && URL_WITH_CREDS_RE.test(value)) return true // user:pass@ in a URL
  if (/^(user(name)?|login|host(name)?|port|url|uri|endpoint|database|db|description|note|message|hint)$/i.test(k)) return false
  if (SECRET_KEY_RE.test(k)) return true
  return Boolean(parentKey && /credential/i.test(String(parentKey)))
}

function scalar(v) {
  return v === null || v === undefined || ['string', 'number', 'boolean'].includes(typeof v)
}

function scalarText(v) {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'string' ? v : String(v)
}

/**
 * Flatten one section object into `{ label, key, value, secret, isUrl, multiline }`
 * rows (depth ≤ 2: nested objects contribute `Parent · child` rows; arrays of
 * scalars join with newlines; deeper structures are JSON-rendered).
 */
export function kvRows(obj, parentKey) {
  const rows = []
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return rows
  const push = (key, value, labelPrefix) => {
    const label = (labelPrefix ? `${labelPrefix} · ` : '') + connectionInfoTitle(key)
    let text
    let multiline = false
    if (scalar(value)) text = scalarText(value)
    else if (Array.isArray(value) && value.every(scalar)) { text = value.map(scalarText).join('\n'); multiline = value.length > 1 }
    else { text = JSON.stringify(value, null, 2); multiline = true }
    const secret = isSecretField(key, typeof value === 'string' ? value : text, parentKey || labelPrefix)
    rows.push({ key: labelPrefix ? `${labelPrefix}.${key}` : key, label, value: text, secret, isUrl: !secret && typeof value === 'string' && URL_RE.test(value.trim()), multiline })
  }
  Object.entries(obj).forEach(([key, value]) => {
    if (MASK_BOOKKEEPING.has(key)) return
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const entries = Object.entries(value).filter(([k2]) => !MASK_BOOKKEEPING.has(k2))
      if (entries.length && entries.every(([, v]) => scalar(v) || (Array.isArray(v) && v.every(scalar)))) {
        entries.forEach(([k2, v2]) => push(k2, v2, connectionInfoTitle(key)))
        return
      }
    }
    push(key, value)
  })
  return rows
}

function serviceRows(services) {
  if (!services || typeof services !== 'object') return []
  return Object.entries(services).filter(([name]) => !MASK_BOOKKEEPING.has(name)).map(([name, s]) => {
    const svc = s && typeof s === 'object' ? s : {}
    const ports = Array.isArray(svc.ports) ? svc.ports.map((p) => {
      const port = p && typeof p === 'object' ? p : {}
      return {
        name: port.name != null ? String(port.name) : '',
        port: port.port != null ? port.port : null,
        targetPort: port.target_port != null ? port.target_port : null,
        protocol: port.protocol || 'TCP',
        nodePort: port.node_port != null ? port.node_port : null
      }
    }) : []
    return {
      name,
      type: svc.type || '—',
      internalHost: svc.internal_host || null,
      clusterIp: svc.cluster_ip || null,
      externalIp: svc.loadbalancer_ip || svc.vip_address || null,
      dnsHostname: svc.dns_hostname ? (svc.dns_domain && !String(svc.dns_hostname).endsWith(svc.dns_domain) ? `${svc.dns_hostname}.${svc.dns_domain}` : svc.dns_hostname) : null,
      ports
    }
  })
}

function ingressRows(ingresses) {
  if (!ingresses || typeof ingresses !== 'object') return []
  return Object.entries(ingresses)
    .filter(([name, url]) => !MASK_BOOKKEEPING.has(name) && typeof url === 'string' && url.trim())
    .map(([name, url]) => {
      let host = null
      let path = null
      try {
        const u = new URL(url)
        host = u.host
        path = u.pathname || '/'
      } catch (_) { /* leave nulls */ }
      return { name, url: url.trim(), host, path }
    })
}

/**
 * Normalise the connection-info builder payload (VERBATIM from cluster-api)
 * into ordered sections the modal renders without knowing the template:
 *   { key, title, kind: 'overview'|'ingress'|'services'|'dns'|'credentials'|'kv'|'note',
 *     rows | items, secretCount, note?, warning? }
 * - `overview`: connectivity status + ready/total pods (+ deployment name).
 * - `cs_application`: its `all_ingresses` → an `ingress` section, its
 *   `internal_url(s)` → `kv` rows, its `services` (no-ingress fallback) →
 *   `services`; its `message` → a `note`.
 * - `services` / `ingresses` (generic fallback) → typed sections.
 * - `dns_configuration` → `dns` rows `{ hostname, ip }` + instructions/warning.
 * - `credentials` → `credentials` rows (all secret unless username-like).
 * - every other object → `kv` rows; scalars at the top level are folded into
 *   the overview. Tolerant of missing keys; never throws.
 */
export function connectionInfoSections(payload) {
  const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}
  const sections = []
  const byKey = {}
  const add = (section) => { byKey[section.key] = section; sections.push(section) }
  const secretCount = (rows) => rows.filter((r) => r.secret).length
  // What a section reports about itself. The SERVER's `masked_keys` is the
  // authority on how many values it hid; the client-side `secret` flag stays
  // only as a styling hint for rows the server did not have to touch.
  const maskFacts = (raw, rows) => {
    const hidden = maskedKeyCount(raw)
    const masked = sectionIsMasked(raw) || hidden > 0
    return { masked, maskedCount: hidden, secretCount: masked ? hidden : secretCount(rows) }
  }

  // Overview
  const overviewRows = []
  if (typeof p.connectivity_status === 'string') overviewRows.push({ key: 'status', label: 'Status', value: p.connectivity_status, secret: false, isUrl: false, multiline: false })
  if (p.pods && typeof p.pods === 'object' && (p.pods.total != null || p.pods.ready != null)) {
    overviewRows.push({ key: 'pods', label: 'Pods ready', value: `${p.pods.ready ?? '—'} / ${p.pods.total ?? '—'}`, secret: false, isUrl: false, multiline: false })
  }
  if (typeof p.deployment_name === 'string' && p.deployment_name) overviewRows.push({ key: 'deployment_name', label: 'Deployment', value: p.deployment_name, secret: false, isUrl: false, multiline: false })
  Object.entries(p).forEach(([k, v]) => {
    if (CI_META_KEYS.has(k) || !scalar(v)) return
    overviewRows.push({ key: k, label: connectionInfoTitle(k), value: scalarText(v), secret: isSecretField(k, v), isUrl: typeof v === 'string' && URL_RE.test(v.trim()), multiline: false })
  })
  if (overviewRows.length) add({ key: 'overview', title: 'Overview', kind: 'overview', rows: overviewRows, secretCount: secretCount(overviewRows) })

  // cs_application
  const app = p.cs_application && typeof p.cs_application === 'object' ? p.cs_application : null
  if (app) {
    const ings = ingressRows(app.all_ingresses || (app.ingress_url ? { [app.ingress_name || 'ingress']: app.ingress_url } : null))
    const internal = {}
    if (app.internal_urls && typeof app.internal_urls === 'object') Object.assign(internal, app.internal_urls)
    else if (typeof app.internal_url === 'string') internal[app.ingress_name || 'internal'] = app.internal_url
    const rows = Object.entries(internal).map(([k, v]) => ({ key: `internal.${k}`, label: `Internal · ${k}`, value: scalarText(v), secret: false, isUrl: false, multiline: false }))
    add({
      key: 'cs_application', title: 'Application', kind: 'ingress', items: ings, rows,
      secretCount: 0,
      note: typeof app.message === 'string' ? app.message : (ings.length ? null : (typeof app.description === 'string' ? app.description : null))
    })
    if (!ings.length && app.services && typeof app.services === 'object') {
      add({ key: 'cs_application.services', title: 'Services', kind: 'services', items: serviceRows(app.services), rows: [], secretCount: 0 })
    }
  }

  // Generic fallback sections
  if (p.ingresses && typeof p.ingresses === 'object' && !byKey.cs_application) {
    const items = ingressRows(p.ingresses)
    if (items.length) add({ key: 'ingresses', title: 'Ingress', kind: 'ingress', items, rows: [], secretCount: 0 })
  }
  if (p.services && typeof p.services === 'object') {
    const items = serviceRows(p.services)
    if (items.length) add({ key: 'services', title: 'Services', kind: 'services', items, rows: [], secretCount: 0 })
  }

  // DNS
  const dns = p.dns_configuration && typeof p.dns_configuration === 'object' ? p.dns_configuration : null
  if (dns) {
    let items = []
    if (dns.hostname_to_ip && typeof dns.hostname_to_ip === 'object') {
      items = Object.entries(dns.hostname_to_ip)
        .filter(([hostname]) => !MASK_BOOKKEEPING.has(hostname))
        .map(([hostname, ip]) => ({ hostname, ip: scalarText(ip) }))
    } else if (Array.isArray(dns.records)) {
      items = dns.records.map((r) => {
        const [hostname, ip] = String(r).split('->').map((x) => x.trim())
        return { hostname, ip: ip || '—' }
      })
    } else if (Array.isArray(dns.hostnames)) {
      items = dns.hostnames.map((h) => ({ hostname: String(h), ip: scalarText(dns.external_ip || dns.haproxy_ip) }))
    }
    add({
      key: 'dns_configuration', title: 'DNS configuration', kind: 'dns', items, rows: [],
      secretCount: 0,
      note: typeof dns.instructions === 'string' ? dns.instructions : null,
      warning: typeof dns.warning === 'string' ? dns.warning : null
    })
  }

  // Everything else → kv (credentials last)
  const handled = new Set(['cs_application', 'ingresses', 'services', 'dns_configuration', 'credentials', ...CI_META_KEYS])
  Object.entries(p).forEach(([key, value]) => {
    if (handled.has(key) || !value || typeof value !== 'object') return
    const rows = Array.isArray(value)
      ? (value.every(scalar) ? [{ key, label: connectionInfoTitle(key), value: value.map(scalarText).join('\n'), secret: isSecretField(key, null), isUrl: false, multiline: value.length > 1 }] : kvRows({ [key]: value }))
      : kvRows(value, key)
    if (rows.length) add({ key, title: connectionInfoTitle(key), kind: 'kv', rows, ...maskFacts(value, rows) })
  })
  if (p.credentials && typeof p.credentials === 'object') {
    // Under `credentials` every non-username-like field is a secret (isSecretField via parentKey).
    const rows = kvRows(p.credentials, 'credentials')
    if (rows.length) add({ key: 'credentials', title: 'Credentials', kind: 'credentials', rows, ...maskFacts(p.credentials, rows) })
  }

  // Order: the known-first list, then payload order, credentials last.
  const rank = (s) => {
    if (s.key === 'credentials') return 1000
    const i = CI_ORDER.indexOf(s.key)
    return i === -1 ? 100 : i
  }
  const indexed = sections.map((s, i) => ({ s, i }))
  indexed.sort((a, b) => (rank(a.s) - rank(b.s)) || (a.i - b.i))
  const ordered = indexed.map(({ s }) => s)
  if (!ordered.length && typeof p.message === 'string' && p.message) {
    ordered.push({ key: 'message', title: 'Details', kind: 'note', rows: [], note: p.message, secretCount: 0 })
  }
  return ordered
}

/**
 * Total hidden/secret rows across sections. A fallback headline count only —
 * `secret_masking.masked_count` from the server is the authority when present
 * (see `connectionInfoMasking` in secret-masking.js), and NOTHING here masks a
 * value: cluster-api ships the stubs.
 */
export function connectionInfoSecretCount(sections) {
  return (Array.isArray(sections) ? sections : []).reduce((n, s) => n + (s && s.secretCount ? s.secretCount : 0), 0)
}
